import React, {MouseEvent, PointerEvent, useCallback, useEffect, useId, useLayoutEffect, useRef} from "react";
import ReactDOM from "react-dom";


export type Placement = "top" | "right" | "bottom" | "left";
type ToolTipProps = {
    children: React.ReactNode;
    open?: boolean;
    defaultOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    delayDuration?: number;
    closeDelay?: number;
}
type ToolTipTriggerProps = React.HTMLAttributes<HTMLElement> & {
    asChild?: boolean;
}
type ToolTipContentProps = {
    children: React.ReactNode;
    placement?: Placement;
    offset?: number;
    portal?: boolean;
    className?: string;
    arrow?: boolean;
}
const ToolTipContext = React.createContext<{
    open: boolean;
    setOpen: (open: boolean) => void;
    delay: number;
    closeDelay: number;
    triggerRef: React.RefObject<HTMLElement | null>;
    toolTipId: string;
} | null>(null)

export const ToolTip = ({
                            children,
                            open: openProp,
                            defaultOpen = false,
                            onOpenChange,
                            delayDuration = 200,
                            closeDelay = 120,
                        }: ToolTipProps) => {
    const [openUncontrolled, setOpenUncontrolled] = React.useState(defaultOpen);
    const isControlled = openProp !== undefined;
    const open = isControlled ? !!openProp : openUncontrolled;

    const setOpen = (open: boolean) => {
        if (!isControlled) setOpenUncontrolled(open);
        onOpenChange?.(open)
    }
    const triggerRef = useRef<HTMLElement>(null)
    const toolTipId = useId()
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false)
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open])
    useEffect(() => {
        if (!open) return;
        const onPointerDown = (e: TouchEvent | MouseEvent) => {
            const tr = triggerRef.current;
            if (tr && (e.target instanceof Node) && !tr.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onPointerDown as any);
        document.addEventListener("touchstart", onPointerDown);
        return (() => {
            document.removeEventListener("mousedown", onPointerDown as any);
            document.removeEventListener("touchstart", onPointerDown);
        })
    }, [open])
    return (
        <ToolTipContext.Provider value={{
            open,
            setOpen,
            delay: delayDuration,
            closeDelay,
            triggerRef,
            toolTipId
        }}>
            {children}
        </ToolTipContext.Provider>
    )
}
export const ToolTipTrigger = ({asChild = false, children, ...rest}: ToolTipTriggerProps) => {
    const ctx = React.useContext(ToolTipContext);
    if (!ctx) throw new Error("ToolTipTrigger must be used within <ToolTipT/>");
    const {setOpen, delay, closeDelay, triggerRef, toolTipId} = ctx;
    const openTimer = useRef<number>(null)
    const closeTimer = useRef<number>(null)
    const clearTimers = () => {
        if (openTimer.current) window.clearTimeout(openTimer.current);
        if (closeTimer.current) window.clearTimeout(closeTimer.current);
        openTimer.current = null;
        closeTimer.current = null
    }
    const handleOpen = () => {
        clearTimers();
        openTimer.current = window.setTimeout(() => setOpen(true), delay)
    }
    const handleClose = () => {
        clearTimers();
        closeTimer.current = window.setTimeout(() => setOpen(false), closeDelay)
    }
    const props: React.HTMLAttributes<HTMLElement> = {
        onMouseEnter: (e) => {
            rest.onMouseEnter?.(e as any);
            handleOpen()
        },
        onMouseLeave: (e) => {
            rest.onMouseLeave?.(e as any);
            handleClose()
        },
        onFocus: (e) => {
            rest.onFocus?.(e as any);
            handleOpen()
        },
        onBlur: (e) => {
            rest.onBlur?.(e as any);
            handleClose()
        },
        onClick: (e) => {
            rest?.onClick?.(e as any);
            if ("ontouchstart" in window) {
                e.preventDefault()
                // setOpen((prev) => !prev);
            }
        },
        ...rest
    }
    return (
        <button type={"button"} ref={triggerRef} {...props as any} className={"inline-flex items-center"}>
            {children}
        </button>
    )
}
export const ToolTipContent = ({
                                   children,
                                   offset = 8,
                                   className,
                                   portal = true,
                                   placement = "top",
                                   arrow = true
                               }: ToolTipContentProps) => {
    const ctx = React.useContext(ToolTipContext);
    if (!ctx) throw new Error("TooltipContent must be used within <Tooltip>");
    const {open, triggerRef, toolTipId} = ctx;
    const [style, setStyle] = React.useState<React.CSSProperties>({});
    const contentRef = useRef<HTMLDivElement>(null)

    const handlePositioning = useCallback(() => {
        const tr = triggerRef.current;
        const el = contentRef.current;
        if (!open || !tr || !el) return;

        const rect = tr.getBoundingClientRect();
        const cRect = el.getBoundingClientRect();

        let top = 0, left = 0;
        switch (placement) {
            case "top":
                top = rect.top - cRect.height - offset;
                left = rect.left + (rect.width - cRect.width) / 2;
                break;
            case "bottom":
                top = rect.top + cRect.height + offset;
                left = rect.left + (rect.width - cRect.width) / 2;
                break;
            case "left":
                top = rect.top + (rect.height - cRect.height) / 2;
                left = rect.left - cRect.width - 1;
                break
            case "right":
                top = rect.top + rect.height / 2 - cRect.height / 2;
                left = rect.right + offset;
                break;
        }
        setStyle({
            position: portal ? "fixed" : "absolute",
            top,
            left,
        })
    }, [offset, open, placement, portal, triggerRef])
    useEffect(() => {
            if (!open) return;

            const handleScroll = (e: Event) => {
                handlePositioning()
            };
            const handleResize = () => handlePositioning();

            window.addEventListener("resize", handleResize);
            window.addEventListener("scroll", handleScroll, true);

            return () => {
                window.removeEventListener("resize", handleResize)
                window.removeEventListener("scroll", handleScroll, true)
            }
        },
        [open, handlePositioning])
    useLayoutEffect(() => {
        if (open) handlePositioning();
    }, [open, placement, offset, portal, handlePositioning]);

    const body = (
        <div
            ref={contentRef}
            id={toolTipId}
            role={"tooltip"}
            data-state={open ? "open" : "closed"}
            className={[
                "z-[50000000] select-none max-w-[280px] rounded-md border border-black/10 bg-neutral-600 px-2 py-1 text-[12px] text-white shadow-lg",
                "data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95",
                "data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=closed]:zoom-out-95",
                "will-change-transform",
                className,
            ].join(" ")}
            style={style}>
            {children}
            {arrow && (
                <span
                    aria-hidden
                    className={[
                        "absolute block size-1.5 rotate-45 bg-neutral-600",
                        placement === "top" && "-bottom-1 left-1/2 -translate-x-1/2",
                        placement === "bottom" && "-top-1 left-1/2 -translate-x-1/2",
                        placement === "left" && "-right-1 top-1/2 -translate-y-1/2",
                        placement === "right" && "-left-1 top-1/2 -translate-y-1/2",
                    ].filter(Boolean).join(" ")}
                />
            )}
        </div>
    )
    if (!open) return null;
    return portal ? ReactDOM.createPortal(body, document.body) : body;
}